/**
 * ENHANCED SKY at night: which sky, over where, on what date — and where every
 * star and the moon end up in it. Pure arithmetic, the same split
 * `enhanced-sky-look.ts` keeps against `enhanced-sky.ts`; `night-sky.ts` turns
 * what comes out of here into meshes.
 *
 * `sky-astronomy.ts` is the ephemeris and `bright-stars.ts` is the catalogue.
 * This module is the part that is about *this game*: it decides what date and
 * what place a sortie is flown at, walks the catalogue through the ephemeris
 * for that moment, and hands back a list of bearings with a palette entry and
 * a size against each one.
 *
 * ## Where the date comes from, in three cases
 *
 * 1. **A career sortie** has a real one. `campaign/calendar.ts` stores the
 *    pilot's clock as a month index and a day, month 1 being December 1915
 *    (`ROSTER.DAT`'s own encoding), and every month is 28 days — the
 *    original's simplification, so day 1-28 maps straight onto a real calendar
 *    day of the same number.
 * 2. **A single mission** briefs an era rather than a date, so the era's own
 *    enlistment month (the three dates `PS.EXE` offers) is taken and the
 *    mission's **seed** picks the day inside it. That is deterministic by
 *    construction: the same `?m=…&seed=…` URL is the same night sky, for the
 *    same reason it is already the same cloud field.
 * 3. **A dogfight or a duel** briefs nothing at all and gets `FALLBACK_NIGHT`,
 *    one fixed WW1-era night — see its own note for why that night.
 *
 * ## And the place
 *
 * The five `MissionRegion`s are `RBMB.EXE`'s own start locations and they are
 * real towns, so they get their real latitudes and longitudes. Latitude is
 * what actually shows: at 51 degrees north the Plough never sets and Orion
 * never gets more than 30 degrees up, and both of those are things a person
 * who has looked at the sky will notice immediately if they are wrong.
 * Longitude only shifts the clock, and it is used for exactly that — the
 * briefed hour is read as **local mean solar time**, so "night" is the middle
 * of the night above the aerodrome rather than the middle of the night at
 * Greenwich.
 */

import type { CareerDate, MissionEra, MissionRegion, TimeOfDay, Vec3 } from "@red-opus/engine";
import { DAYS_PER_MONTH, ENLISTMENT_DATES, monthOfYearOf, yearOf } from "@red-opus/engine";
import { brightStars, CATALOGUE_MAGNITUDE_LIMIT } from "./bright-stars.ts";
import { blendExponentForElevation, mixPalette } from "./enhanced-sky-look.ts";
import {
  directionOf,
  horizontalOf,
  julianDay,
  moonPhaseOf,
  moonPosition,
  precessFromJ2000,
  sunPosition,
  type MoonPhase,
  type Observer,
} from "./sky-astronomy.ts";
import { CELESTIAL_HOURS, type PaletteColour } from "./time-of-day.ts";
import { DEFAULT_FLY_REGION } from "../world/region-catalog.ts";

/**
 * The five start locations, at their real coordinates. Dunkirk, Paris and
 * London are the towns; "Somme" and "Verdun" are sectors rather than points,
 * so they are pinned at Albert and at Verdun-sur-Meuse — the aerodrome towns
 * the campaign's own `AERO.DAT` fields cluster around.
 */
export const REGION_OBSERVERS: Readonly<Record<MissionRegion, Observer>> = {
  london: { latitudeDeg: 51.507, longitudeDeg: -0.128 },
  dunkirk: { latitudeDeg: 51.035, longitudeDeg: 2.377 },
  somme: { latitudeDeg: 50.001, longitudeDeg: 2.653 },
  paris: { latitudeDeg: 48.857, longitudeDeg: 2.352 },
  verdun: { latitudeDeg: 49.16, longitudeDeg: 5.388 },
};

/** A calendar date and an hour of local mean solar time, over one place. */
export interface NightSkyMoment {
  readonly year: number;
  /** 1-12. */
  readonly month: number;
  /** 1-28, since that is all the career calendar has. */
  readonly day: number;
  /** Local mean solar time, hours. `time-of-day.ts`'s `CELESTIAL_HOURS` supplies it. */
  readonly localHour: number;
  readonly observer: Observer;
}

/**
 * The night a sortie that briefs nothing is flown under: **16 December 1915**,
 * midnight over Verdun — the region a briefing-less flight already loads
 * (`region-catalog.ts`'s `DEFAULT_FLY_REGION`).
 *
 * Chosen rather than picked at random, and the three reasons are all things a
 * screenshot can be checked against. December 1915 is *month 1* of the career
 * calendar, so the default sky is the war's own first month rather than an
 * arbitrary date. At that midnight Orion stands due south at 40 degrees and
 * the Plough is 45 degrees up in the north-east — the two landmarks in one
 * frame, neither of them at the horizon, which is the classic winter sky at
 * this latitude. And the moon was a **waxing gibbous, 74 per cent lit**, 26
 * degrees up in the west: full enough to be obviously the moon, and far enough
 * from full to show an unmistakable terminator.
 */
export const FALLBACK_NIGHT: NightSkyMoment = {
  year: 1915,
  month: 12,
  day: 16,
  localHour: CELESTIAL_HOURS.night,
  observer: REGION_OBSERVERS[DEFAULT_FLY_REGION],
};

/** What `/fly` knows about when and where a sortie happens. Every field is optional; see the module doc. */
export interface NightSkyBrief {
  readonly timeOfDay?: TimeOfDay;
  readonly region?: MissionRegion;
  readonly era?: MissionEra;
  /** `MissionSpec.seed` — picks the day of the month for a single mission. */
  readonly seed?: number;
  /** A career pilot's own clock, which beats the era when it is there. */
  readonly date?: CareerDate;
}

/** The three `MissionEra` words onto the three enlistment months `PS.EXE` offers. */
const ERA_MONTHS: Readonly<Record<MissionEra, CareerDate>> = {
  early: ENLISTMENT_DATES.early,
  mid: ENLISTMENT_DATES.middle,
  late: ENLISTMENT_DATES.late,
};

/** The moment a brief is flown at. Deterministic in every branch — the same brief is the same sky. */
export function nightSkyMomentFor(brief: NightSkyBrief): NightSkyMoment {
  const observer = REGION_OBSERVERS[brief.region ?? DEFAULT_FLY_REGION];
  const localHour = CELESTIAL_HOURS[brief.timeOfDay ?? "night"];
  const era = brief.era ? ERA_MONTHS[brief.era] : undefined;
  const date = brief.date ?? era;
  if (!date) return { ...FALLBACK_NIGHT, localHour, observer };
  // A career date carries its own day; an era only names a month, so the
  // mission's seed picks the day inside it.
  const day = brief.date ? brief.date.day : (Math.abs(Math.trunc(brief.seed ?? 0)) % DAYS_PER_MONTH) + 1;
  return { year: yearOf(date), month: monthOfYearOf(date) + 1, day, localHour, observer };
}

/**
 * The Julian Day the ephemeris wants. The briefed hour is local mean solar
 * time, so the observer's longitude is what turns it into UT — 15 degrees an
 * hour, the same rate `sun.c`'s own hour angle runs at, which is the one place
 * the original's clock and this one agree exactly.
 */
export function julianDayOf(moment: NightSkyMoment): number {
  const universalHour = moment.localHour - moment.observer.longitudeDeg / 15;
  return julianDay(moment.year, moment.month, moment.day + universalHour / 24);
}

/**
 * Below this elevation a star has faded fully into the horizon colour. Twelve
 * degrees, the same band `star-field.ts` fades the original's own points over
 * and the same one `TWILIGHT_CONCENTRATION` spreads its wash across — one
 * number for "how thick the haze is", not three.
 */
export const STAR_FADE_ELEVATION_DEG = 12;

/**
 * The four `BARON.PAL` star greys, 198 down to 195, brightest first. These are
 * the original's own star colours (`tools/bake/src/stages/stars.ts` reads them
 * out of `STARS.DAT`'s palette indices) and they are reused rather than
 * replaced: the run gets bluer as it gets dimmer, which is what a faint star
 * looks like, and it keeps this layer inside the palette like every other.
 */
export const STAR_GREYS: readonly PaletteColour[] = [0xb2b2b2, 0x9eaeb6, 0x86aaba, 0x71aac3];

/** An original pixel of a 200-line frame, for the faintest star drawn. */
export const STAR_MIN_PIXELS = 1;
/**
 * And for Sirius. Bigger than the original's flat one pixel, because a
 * catalogue field has 518 points in it and a magnitude scale that is only a
 * brightness scale reads as noise — size is what makes a constellation a
 * *shape*. Kept small enough that the brightest star is still a point.
 */
export const STAR_MAX_PIXELS = 2.2;

/** The dimmest a star is drawn, as a fraction of the way from the sky colour to its own grey. */
const STAR_MIN_BRIGHTNESS = 0.3;

/** The brightest star in the catalogue (Sirius). The magnitude scale is stretched between this and the limit. */
export const BRIGHTEST_CATALOGUE_MAGNITUDE = -1.46;

/**
 * Where a magnitude falls on `[0, 1]`, 1 being Sirius and 0 being the
 * catalogue's own limit. Both the size and the brightness ramps ride this, so
 * a star cannot be big and dim.
 */
export function magnitudeRank(magnitude: number): number {
  const span = CATALOGUE_MAGNITUDE_LIMIT - BRIGHTEST_CATALOGUE_MAGNITUDE;
  return Math.min(Math.max((CATALOGUE_MAGNITUDE_LIMIT - magnitude) / span, 0), 1);
}

/** One star, resolved into world axes with its fade and its magnitude already folded in. */
export interface PlacedNightStar {
  readonly direction: Vec3;
  readonly colour: PaletteColour;
  /** Side of the star's quad, in original 200-line pixels. */
  readonly sizePx: number;
}

/** The moon, when it is up: where it is, which way the sun is, and how much of it is lit. */
export interface PlacedMoon {
  readonly direction: Vec3;
  /** The sun's bearing — below the horizon at night, and the only thing that says which limb is lit. */
  readonly sunDirection: Vec3;
  readonly phase: MoonPhase;
}

/** Everything the night half of the enhanced sky draws. */
export interface NightSky {
  readonly moment: NightSkyMoment;
  readonly stars: readonly PlacedNightStar[];
  /** `undefined` when the moon is below the horizon, which is most nights for part of the night. */
  readonly moon: PlacedMoon | undefined;
  /** The moon's bearing whether or not it is up — what the dome hangs its halo on. */
  readonly moonDirection: Vec3;
}

/**
 * The dome a star has to be seen against — `EnhancedSkyStops`' own three
 * gradient stops and the two elevations it blends them at.
 *
 * All five, rather than just the two ends, because a star's colour here is a
 * blend **from the sky behind it** toward its own grey, and the sky behind it
 * is whatever `enhanced-sky.ts`'s `gradient()` painted at that elevation. Given
 * only the ends, `skyBehindAt` would have to guess the middle, and the guess is
 * not harmless: the night dome's mid tone is `BARON.PAL` 1 and a faint star
 * blended up from *black* against it comes out **darker than the sky it is
 * drawn on** — a black dot where a star should be. Reproducing the same two
 * blends is what makes a star brighter than its own background everywhere.
 */
export interface NightSkyPaint {
  readonly zenith: PaletteColour;
  readonly mid: PaletteColour;
  readonly horizon: PaletteColour;
  /** `EnhancedSkyStops.midElevationDeg` — where mid has blended half-way into zenith. */
  readonly midElevationDeg: number;
  /** `EnhancedSkyStops.hazeElevationDeg` — where mid has blended half-way into horizon. */
  readonly hazeElevationDeg: number;
  /**
   * How much of the catalogue this night's cloud lets through, as a magnitude.
   * `CATALOGUE_MAGNITUDE_LIMIT` is a clear night; a lower number is cloud
   * taking the faint end away, which is what cloud does.
   */
  readonly limitMagnitude: number;
}

/**
 * The dome's own colour at one elevation, in palette space: `gradient()` from
 * `enhanced-sky.ts`, arithmetic instead of TSL, and without the bearing-picked
 * warm horizon (night has none — `NIGHT_STOPS` sets both horizons the same).
 */
export function skyBehindAt(sinElevation: number, paint: NightSkyPaint): PaletteColour {
  const up = Math.min(Math.max(sinElevation, 0), 1);
  const towardsZenith = up ** blendExponentForElevation(paint.midElevationDeg, false);
  const towardsHorizon = (1 - up) ** blendExponentForElevation(paint.hazeElevationDeg, true);
  return mixPalette(mixPalette(paint.mid, paint.horizon, towardsHorizon), paint.zenith, towardsZenith);
}

/**
 * The whole night sky for one brief.
 *
 * Stars below the horizon are dropped here rather than drawn and hidden: half
 * the catalogue is under the ground at any moment, and the half that is not is
 * still 250-odd quads.
 */
export function nightSkyFor(brief: NightSkyBrief, paint: NightSkyPaint): NightSky {
  const moment = nightSkyMomentFor(brief);
  const jd = julianDayOf(moment);
  const fadeSine = Math.sin((STAR_FADE_ELEVATION_DEG * Math.PI) / 180);

  const stars: PlacedNightStar[] = [];
  for (const star of brightStars()) {
    if (star.magnitude > paint.limitMagnitude) continue;
    const position = precessFromJ2000(
      { rightAscensionDeg: star.rightAscensionDeg, declinationDeg: star.declinationDeg },
      jd,
    );
    const direction = directionOf(horizontalOf(position, jd, moment.observer));
    if (direction.y <= 0) continue;
    const rank = magnitudeRank(star.magnitude);
    const fade = Math.min(direction.y / fadeSine, 1);
    const brightness = (STAR_MIN_BRIGHTNESS + (1 - STAR_MIN_BRIGHTNESS) * rank) * fade;
    stars.push({
      direction,
      // Blended **up from the sky it is drawn on**, so a star is brighter than
      // its own background at every elevation and never a dark dot; `fade`
      // takes it all the way back down to that background as it nears the
      // skyline, which is the haze the original's own field also fades into.
      colour: mixPalette(skyBehindAt(direction.y, paint), STAR_GREYS[greyIndexOf(rank)] ?? 0, brightness),
      sizePx: STAR_MIN_PIXELS + (STAR_MAX_PIXELS - STAR_MIN_PIXELS) * rank,
    });
  }

  const sun = sunPosition(jd);
  const moon = moonPosition(jd);
  const moonDirection = directionOf(horizontalOf(moon, jd, moment.observer));
  const sunDirection = directionOf(horizontalOf(sun, jd, moment.observer));
  return {
    moment,
    stars,
    moonDirection,
    moon:
      moonDirection.y > 0
        ? { direction: moonDirection, sunDirection, phase: moonPhaseOf(sun, moon) }
        : undefined,
  };
}

/** Which of the four greys a rank lands on — brightest quarter gets 198, faintest gets 195. */
function greyIndexOf(rank: number): number {
  const at = Math.floor((1 - rank) * STAR_GREYS.length);
  return Math.min(Math.max(at, 0), STAR_GREYS.length - 1);
}

/**
 * How deep into the catalogue this sky word lets a pilot see. Clear is the
 * whole of it; the greys the words already mix the dome toward
 * (`SKY_WORD_TREATMENTS.greyMix`) are reused as the fraction of the magnitude
 * range that goes away, so an overcast night keeps only the handful of stars
 * that would actually burn through — and `sky-clouds.ts` draws no slab at all
 * under that word, so this is the only thing that says the sky is covered.
 */
export function limitMagnitudeFor(greyMix: number): number {
  const span = CATALOGUE_MAGNITUDE_LIMIT - BRIGHTEST_CATALOGUE_MAGNITUDE;
  return CATALOGUE_MAGNITUDE_LIMIT - span * Math.min(Math.max(greyMix, 0), 1);
}
